import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtMotorChecks: CorrelationValidationGenerator = (
  correlationLimits,
  correlationField,
  { calcs }
) => [
  {
    name: `${correlationField}_oil_gravity_motor`,
    message: `API Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_API,
      correlationLimits.MAX_API,
      calcs.basicData.value.oilAPI
    ]),
    isInvalid:
      calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
      calcs.basicData.value.oilAPI < correlationLimits.MIN_API,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_gas_specific_gravity_motor`,
    message: `Gas Specific Gravity Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_GAS_SG,
      correlationLimits.MAX_GAS_SG,
      calcs.basicData.value.gasSpecificGravity
    ]),
    isInvalid:
      calcs.basicData.value.gasSpecificGravity > correlationLimits.MAX_GAS_SG ||
      calcs.basicData.value.gasSpecificGravity < correlationLimits.MIN_GAS_SG,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_temperature_motor`,
    message: `Temperature Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_TEMP,
      correlationLimits.MAX_TEMP,
      calcs.motorCalculations.downholeMotorTemperatureF.value
    ]),
    isInvalid:
      correlationLimits.MAX_TEMP <
        calcs.motorCalculations.downholeMotorTemperatureF.value ||
      correlationLimits.MIN_TEMP >
        calcs.motorCalculations.downholeMotorTemperatureF.value,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_pressure_motor`,
    message: `Pressure Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PRESS,
      correlationLimits.MAX_PRESS,
      calcs.motorCalculations.wellFlowingPressureAtMotorPSIA.value
    ]),
    isInvalid:
      calcs.motorCalculations.wellFlowingPressureAtMotorPSIA.value >
        correlationLimits.MAX_PRESS ||
      calcs.motorCalculations.wellFlowingPressureAtMotorPSIA.value <
        correlationLimits.MIN_PRESS,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_oil_solution_gas_motor`,
    message: `Oil Solution Gas Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_RSO,
      correlationLimits.MAX_RSO,
      calcs.fluidProperties.fluidPropertiesAtMotor.value
        .oilPhaseGasSolubilitySCFSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value
        .oilPhaseGasSolubilitySCFSTB > correlationLimits.MAX_RSO ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value
        .oilPhaseGasSolubilitySCFSTB < correlationLimits.MIN_RSO,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_bubblepoint_pressure_motor`,
    message: `Bubblepoint Pressure Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PB,
      correlationLimits.MAX_PB,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.bubblePointPressurePSIA
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value
        .bubblePointPressurePSIA > correlationLimits.MAX_PB ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value
        .bubblePointPressurePSIA < correlationLimits.MIN_PB,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_oil_formation_volume_factor_motor`,
    message: `Oil Formation Volume Factor Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_BO,
      correlationLimits.MAX_BO,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilVolumeFactorBBLSTB
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilVolumeFactorBBLSTB >
        correlationLimits.MAX_BO ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilVolumeFactorBBLSTB <
        correlationLimits.MIN_BO,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_dead_oil_viscosity_motor`,
    message: `Dead Oil Viscosity Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_U_OD,
      correlationLimits.MAX_U_OD,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.deadOilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value.deadOilViscosityCP >
        correlationLimits.MAX_U_OD ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value.deadOilViscosityCP <
        correlationLimits.MIN_U_OD,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_live_oil_viscosity_motor`,
    message: `Live Oil Viscosity Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_U_O,
      correlationLimits.MAX_U_O,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilViscosityCP
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilViscosityCP >
        correlationLimits.MAX_U_O ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value.oilViscosityCP <
        correlationLimits.MIN_U_O,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_pseudo_reduced_pressure_motor`,
    message: `Pseudo-Reduced Pressure Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_PPR,
      correlationLimits.MAX_PPR,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedPressure
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedPressure > correlationLimits.MAX_PPR ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedPressure < correlationLimits.MIN_PPR,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: `${correlationField}_pseudo_reduced_temperature_motor`,
    message: `Pseudo-Reduced Temperature Out of Range @ Motor`,
    severity: Severity.warn,
    isActive: valuesExist([
      correlationLimits.MIN_TPR,
      correlationLimits.MAX_TPR,
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedTemperature
    ]),
    isInvalid:
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedTemperature > correlationLimits.MAX_TPR ||
      calcs.fluidProperties.fluidPropertiesAtMotor.value.psuedoCriticals
        .psuedoReducedTemperature < correlationLimits.MIN_TPR,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  }
];
